import { useMemo } from 'react';
import { useTheme, type ThemeNames } from '../../theme';
import { type CSSObject } from '@emotion/react';

type TooltipStyles = {
  /** The popup body box. */
  tooltip: CSSObject;
  /** Applied to the Popper root when `isFaded` is set. */
  faded: CSSObject;
};

export const useTooltipStyles = (
  themeName: ThemeNames = 'dark',
): TooltipStyles => {
  const theme = useTheme(themeName);

  // Memoised so `tooltip`/`faded` keep stable identities across renders — the popup
  // body's `css` and the Popper `sx` depend on these, and the tooltip re-renders
  // several times while the open animation runs.
  return useMemo(
    () => ({
      tooltip: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: theme.spacing(1),
        minHeight: 32,
        gap: theme.spacing(2),
        color: theme.text.generic.secondary,
        backgroundColor: theme.tooltip.background,
        boxShadow: theme.shadows[1],
        backdropFilter: theme.backdropFilter.xlarge,
        borderRadius: theme.shape.radius8,
        fontSize: 15,
        userSelect: 'none',
      },
      faded: {
        opacity: 0.3,
        transition: 'opacity 0.2s ease-in-out',
      },
    }),
    [theme],
  );
};
